import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { get } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Panel } from "@/components/common/Panel";
import { LoadingState, ErrorState } from "@/components/common/States";
import { cn } from "@/lib/utils";

const STATUT_JOUR = { non_planifiee: "Non planifiée", aucune_production: "Aucune production", indisponible: "Indisponible" };

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-popover border border-border rounded-sm p-2 text-[11px] font-mono shadow-xl">
      <div className="font-semibold mb-1">{fmtDate(label)}</div>
      {d.trs == null ? <div className="text-muted-foreground">{STATUT_JOUR[d.statut_jour] || "—"}</div> : (<>
        <div className="text-emerald-400">TRS {d.trs} %</div>
        <div className="text-sky-400">D {d.disponibilite} %</div>
        <div className="text-violet-400">P {d.performance} %</div>
        <div className="text-amber-400">Q {d.qualite} %</div>
      </>)}
    </div>
  );
};

export const TrendChart = ({ ligneId }) => {
  const [jours, setJours] = useState(7);
  const { data, isLoading, error, refetch } = useQuery({ queryKey: ["trs-tendance", jours, ligneId], queryFn: () => get("/trs/tendance", { jours, ligne_id: ligneId || undefined }) });
  return (
    <Panel title="Tendance TRS" subtitle={`${jours} derniers jours`} testId="trend-panel"
      actions={[7, 14, 30].map((j) => (
        <button key={j} onClick={() => setJours(j)} data-testid={`trend-range-${j}`} className={cn("h-6 px-2 text-[11px] font-mono border rounded-sm", jours === j ? "bg-accent border-border text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>{j}j</button>
      ))}>
      {isLoading ? <LoadingState rows={3} /> : error ? <ErrorState error={error} onRetry={refetch} /> : (
        <div className="h-56 p-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fill: "#94a3b8", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fill: "#94a3b8", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
              <Tooltip content={<Tip />} />
              <ReferenceLine y={75} stroke="#10b981" strokeDasharray="4 4" strokeOpacity={0.5} />
              <ReferenceLine y={60} stroke="#ef4444" strokeDasharray="4 4" strokeOpacity={0.5} />
              <Line type="monotone" dataKey="disponibilite" stroke="#3b82f6" strokeWidth={1} dot={false} connectNulls strokeOpacity={0.6} />
              <Line type="monotone" dataKey="performance" stroke="#8b5cf6" strokeWidth={1} dot={false} connectNulls strokeOpacity={0.6} />
              <Line type="monotone" dataKey="qualite" stroke="#f59e0b" strokeWidth={1} dot={false} connectNulls strokeOpacity={0.6} />
              <Line type="monotone" dataKey="trs" stroke="#10b981" strokeWidth={2.5} dot={{ r: 2.5, fill: "#10b981" }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="px-3 pb-2 flex gap-3 text-[10px] font-mono text-muted-foreground">
        <span><i className="inline-block w-2 h-2 bg-emerald-500 mr-1" />TRS</span><span><i className="inline-block w-2 h-2 bg-blue-500 mr-1" />Dispo</span><span><i className="inline-block w-2 h-2 bg-violet-500 mr-1" />Perf</span><span><i className="inline-block w-2 h-2 bg-amber-500 mr-1" />Qualité</span>
      </div>
    </Panel>
  );
};
